import React from "react";
import { useTranslation } from "react-i18next";
import { MoreHorizontal } from "lucide-react";
import { ALL } from "@/lib/crm";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/** Select with an "all" option; `counts` (optional) appends per-option counters. */
export const FilterSelect = ({ value, onChange, options, allLabel, render, counts, testId, className = "w-full sm:w-[170px]" }) => (
  <Select value={value || ALL} onValueChange={onChange}>
    <SelectTrigger className={cn("bg-card", className)} data-testid={testId} aria-label={allLabel}><SelectValue /></SelectTrigger>
    <SelectContent className="og-scroll max-h-72 bg-card">
      <SelectItem value={ALL} data-testid={`${testId}-option-all`}>{allLabel}</SelectItem>
      {options.map((o) => (
        <SelectItem key={o} value={o} data-testid={`${testId}-option-${o}`}>
          {render ? render(o) : o}{counts && counts[o] != null ? <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{counts[o]}</span> : null}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/** Row actions menu. items: [{ key, label, onSelect, destructive, hidden, separator }] */
export const RowMenu = ({ items, testId, label }) => {
  const { t } = useTranslation();
  const visible = items.filter((i) => i && !i.hidden);
  if (!visible.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={label || t("common.actions")} data-testid={testId} onClick={(e) => e.stopPropagation()}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 bg-card" onClick={(e) => e.stopPropagation()}>
        {visible.map((i) => (
          <React.Fragment key={i.key}>
            {i.separator && <DropdownMenuSeparator />}
            <DropdownMenuItem onSelect={i.onSelect} className={cn("cursor-pointer", i.destructive && "text-rose-900 focus:bg-rose-50 focus:text-rose-900")} data-testid={`${testId}-${i.key}`}>{i.label}</DropdownMenuItem>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Clickable table row (keyboard accessible). */
export const rowProps = (onOpen, testId) => ({
  className: "cursor-pointer hover:bg-muted/40 focus-within:bg-muted/40",
  onClick: onOpen,
  onKeyDown: (e) => { if (e.key === "Enter" && e.target === e.currentTarget) onOpen(); },
  tabIndex: 0,
  "data-testid": testId,
});
